import { Building2, Check, LayoutDashboard, UserRound } from "lucide-react";
import Reveal from "@/components/ui/Reveal";

/** The same platform seen from the three seats that buy, run and use it. */
const audiences = [
  {
    Icon: UserRound,
    title: "Employee",
    body: "A learning path built around their role, level and goals.",
    points: ["Starts from what they already know", "Practice drawn from their own work", "Expert feedback on what they build"],
  },
  {
    Icon: LayoutDashboard,
    title: "L&D Team",
    body: "Role-based programs deployed at scale, without building each one by hand.",
    points: ["Assign paths by role and seniority", "Track progress and completion in one dashboard", "See capability gaps as they close"],
  },
  {
    Icon: Building2,
    title: "Organization",
    body: "AI capability built across every team, with proof that it is real.",
    points: ["Learning tied to business goals", "Skills validated by human experts", "Outcomes leadership can measure"],
  },
] as const;

export default function EnterpriseScale() {
  return (
    <section
      id="enterprise-scale"
      aria-labelledby="enterprise-scale-heading"
      className="scroll-mt-20 bg-mkt-surface py-size-xl px-margin-mobile md:px-margin-desktop"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal className="max-w-4xl mb-size-lg">
          <div className="flex items-center gap-base mb-size-sm">
            <span className="w-12 h-[1px] bg-mkt-primary" />
            <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
              Personalization at enterprise scale
            </span>
          </div>
          <h2
            id="enterprise-scale-heading"
            className="font-display-lg text-[clamp(30px,3.4vw,48px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black text-balance"
          >
            One platform. Thousands of personalized learning paths.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
          {audiences.map(({ Icon, title, body, points }, index) => (
            <Reveal key={title} delayMs={[0, 100, 200][index]} className="h-full">
              <article className="group h-full rounded-xl border border-mkt-primary/20 bg-white p-size-md sm:p-size-lg flex flex-col gap-size-md shadow-[0_4px_16px_rgba(0,60,185,0.10)] hover:shadow-[0_8px_30px_rgba(0,60,185,0.18)] hover:border-mkt-primary/40 transition-all duration-300">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-mkt-primary/10 text-mkt-primary transition-transform duration-300 group-hover:scale-105">
                  <Icon aria-hidden="true" strokeWidth={1.5} className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-headline-lg text-headline-sm md:text-headline-md text-mkt-on-surface mb-size-xs">
                    {title}
                  </h3>
                  <p className="font-body-md text-body-md text-mkt-on-surface-variant">{body}</p>
                </div>
                <ul className="list-none pl-0 ml-0 mt-auto flex flex-col gap-size-sm border-t border-mkt-outline-variant/40 pt-size-md">
                  {points.map((point) => (
                    <li key={point} className="flex items-start gap-size-sm font-body-md text-body-md text-mkt-on-surface">
                      <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-mkt-primary" />
                      {point}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
